import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import VideocamIcon from '@mui/icons-material/Videocam';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';

import {
  getCampaignElementById,
  updateCampaignElementValue,
  getCampaignElements,
  getCampaignLandingOrganizationId,
  getCampaignLanguage
} from '../../redux/modules/campaignLandingPage';
import { SUBMISSION_TYPES, SUBMISSION_TYPE_LABELS } from '../../constants/element-types';
import DynamicCollectors from './DynamicCollectors';
import styles from './SubmissionTypeSelectorField.module.less';

const SUBMISSION_TYPE_ICONS = {
  [SUBMISSION_TYPES.VIDEO]: VideocamIcon,
  [SUBMISSION_TYPES.IMAGE]: ImageIcon,
  [SUBMISSION_TYPES.TEXT]: TextFieldsIcon
};

const SubmissionTypeSelectorField = ({
  _updateCampaignElementValue,
  element,
  qid,
  campaignElements,
  organizationId,
  language
}) => {
  const allowedValues = element?.allowedValues || [SUBMISSION_TYPES.VIDEO];
  const selectedTypes = campaignElements[qid]?.selectedSubmissionTypes || [];
  const [isTouched, setIsTouched] = useState(false);

  // Auto-select if only one option is available
  useEffect(() => {
    if (allowedValues.length === 1 && selectedTypes.length === 0) {
      _updateCampaignElementValue({
        qid,
        selectedSubmissionTypes: allowedValues,
        selected: true
      });
    }
  }, [allowedValues, selectedTypes.length, qid, _updateCampaignElementValue]);

  const handleTypeToggle = (typeId, checked) => {
    setIsTouched(true);
    let newSelectedTypes;

    if (checked) {
      newSelectedTypes = [...selectedTypes, typeId];
    } else {
      newSelectedTypes = selectedTypes.filter((t) => t !== typeId);
    }

    _updateCampaignElementValue({
      qid,
      selectedSubmissionTypes: newSelectedTypes,
      selected: newSelectedTypes.length > 0
    });
  };

  const isValid = !element?.required || selectedTypes.length > 0;
  const showError = element?.required && isTouched && !isValid;

  return (
    <section className={styles.vpSubmissionTypeSelectorContainer}>
      <Box className={styles.vpSelectorSection}>
        <label className={styles.vpLabel}>
          {element?.label || 'How would you like to submit?'}
          {element?.required && <span className={styles.vpRequiredLabel}>*</span>}
        </label>

        <Stack direction="row" flexWrap="wrap" gap={1.5} className={styles.vpOptionsContainer}>
          {allowedValues.map((typeId) => {
            const IconComponent = SUBMISSION_TYPE_ICONS[typeId];
            const isChecked = selectedTypes.includes(typeId);
            const label = SUBMISSION_TYPE_LABELS[typeId] || typeId;

            return (
              <Box
                key={typeId}
                className={`${styles.vpOptionCard} ${isChecked ? styles.vpOptionCardSelected : ''}`}
                onClick={() => handleTypeToggle(typeId, !isChecked)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleTypeToggle(typeId, e.target.checked);
                      }}
                      size="small"
                      sx={{ padding: '4px' }}
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {IconComponent && (
                        <IconComponent
                          fontSize="small"
                          color={isChecked ? 'primary' : 'action'}
                        />
                      )}
                      <Typography variant="body2" fontWeight={isChecked ? 600 : 400}>
                        {label}
                      </Typography>
                    </Stack>
                  }
                  sx={{ margin: 0 }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>
            );
          })}
        </Stack>

        {showError && (
          <Typography variant="caption" color="error" className={styles.vpErrorMessage}>
            Please select at least one submission type
          </Typography>
        )}

        {selectedTypes.length > 0 && (
          <Typography variant="caption" color="text.secondary" className={styles.vpHelpText}>
            All selected options are required to complete your submission
          </Typography>
        )}
      </Box>

      {selectedTypes.length > 0 && (
        <DynamicCollectors
          qid={qid}
          selectedTypes={selectedTypes}
          element={element}
          campaignElements={campaignElements}
          organizationId={organizationId}
          language={language}
        />
      )}
    </section>
  );
};

SubmissionTypeSelectorField.propTypes = {
  _updateCampaignElementValue: PropTypes.func,
  element: PropTypes.object,
  qid: PropTypes.string,
  campaignElements: PropTypes.object,
  organizationId: PropTypes.string,
  language: PropTypes.string
};

function mapStateToProps(state, ownProps) {
  return {
    element: getCampaignElementById(state, ownProps.qid),
    campaignElements: getCampaignElements(state),
    organizationId: getCampaignLandingOrganizationId(state),
    language: getCampaignLanguage(state)
  };
}

const mapDispatchToProps = {
  _updateCampaignElementValue: updateCampaignElementValue
};

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionTypeSelectorField);



// WEBPACK FOOTER //
// ./src/components/CampaignLanding/SubmissionTypeSelectorField.jsx